import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MailIcon, PhoneIcon, FacebookIcon, InstagramIcon, LinkedinIcon, SendIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type ContactFormProps = {
  language: Language;
};
const content = {
  ua: {
    title: "Зв'яжіться з нами",
    subtitle: 'Готові відповісти на ваші запитання',
    namePlaceholder: "Ваше ім'я",
    emailPlaceholder: 'Email',
    messagePlaceholder: 'Повідомлення',
    submitButton: 'Надіслати',
    phone: '+49-(0)6221/520440',
    email: 'info@chamaeleon-produktion.de',
    location: 'Rudolf-Diesel-Straße 8a, 69115 Heidelberg, Germany'
  },
  en: {
    title: 'Contact Us',
    subtitle: 'Ready to answer your questions',
    namePlaceholder: 'Your name',
    emailPlaceholder: 'Email',
    messagePlaceholder: 'Message',
    submitButton: 'Send',
    phone: '+49-(0)6221/520440',
    email: 'info@chamaeleon-produktion.de',
    location: 'Rudolf-Diesel-Straße 8a, 69115 Heidelberg, Germany'
  }
};
const socialLinks = [{
  icon: FacebookIcon,
  href: '#',
  label: 'Facebook'
}, {
  icon: InstagramIcon,
  href: '#',
  label: 'Instagram'
}, {
  icon: LinkedinIcon,
  href: '#',
  label: 'LinkedIn'
}];
export function ContactForm({
  language
}: ContactFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = content[language];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };
  return <section ref={ref} className="relative py-16 sm:py-20 lg:py-32 bg-chamaleon-black overflow-hidden" data-id="element-25">
      {/* Background */}
      <div className="absolute inset-0" data-id="element-26">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,184,28,0.3) 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} data-id="element-27" />

        <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.1] pointer-events-none" style={{
        background: 'radial-gradient(circle, #FFB81C 0%, transparent 70%)'
      }} animate={{
        scale: [1, 1.15, 1]
      }} transition={{
        duration: 10,
        repeat: Infinity
      }} data-id="element-28" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-29">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12 lg:mb-16" data-id="element-30">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-3 sm:mb-4" data-id="element-31">
            {t.title}
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 mb-6" data-id="element-32">{t.subtitle}</p>
          <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-chamaleon-yellow to-transparent mx-auto" data-id="element-33" />
        </motion.div>

        {/* Two columns: Contacts + Form */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-34">
          {/* Left: Contact Info */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} className="space-y-6" data-id="element-35">
            {/* Contact details */}
            <motion.a href={`tel:${t.phone}`} className="flex items-center gap-4 group" whileHover={{
            x: 5
          }} data-id="element-36">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-chamaleon-yellow/50 transition-colors flex-shrink-0" data-id="element-37">
                <PhoneIcon className="w-6 h-6 text-chamaleon-yellow" data-id="element-38" />
              </div>
              <div className="min-w-0" data-id="element-39">
                <div className="text-sm text-gray-500 mb-1" data-id="element-40">
                  {language === 'ua' ? 'Телефон' : 'Phone'}
                </div>
                <div className="text-white font-medium group-hover:text-chamaleon-yellow transition-colors truncate" data-id="element-41">
                  {t.phone}
                </div>
              </div>
            </motion.a>

            <motion.a href={`mailto:${t.email}`} className="flex items-center gap-4 group" whileHover={{
            x: 5
          }} data-id="element-42">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-chamaleon-yellow/50 transition-colors flex-shrink-0" data-id="element-43">
                <MailIcon className="w-6 h-6 text-chamaleon-yellow" data-id="element-44" />
              </div>
              <div className="min-w-0" data-id="element-45">
                <div className="text-sm text-gray-500 mb-1" data-id="element-46">Email</div>
                <div className="text-white font-medium group-hover:text-chamaleon-yellow transition-colors truncate" data-id="element-47">
                  {t.email}
                </div>
              </div>
            </motion.a>

            {/* Social links */}
            <div className="pt-6" data-id="element-48">
              <div className="text-sm text-gray-500 mb-4" data-id="element-49">
                {language === 'ua' ? 'Соціальні мережі' : 'Social Media'}
              </div>
              <div className="flex items-center gap-3" data-id="element-50">
                {socialLinks.map(social => {
                const Icon = social.icon;
                return <motion.a key={social.label} href={social.href} aria-label={social.label} whileHover={{
                  scale: 1.1,
                  y: -2
                }} whileTap={{
                  scale: 0.95
                }} className="w-12 h-12 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center text-chamaleon-yellow hover:border-chamaleon-yellow/50 transition-all" data-id="element-51">
                      <Icon className="w-5 h-5" data-id="element-52" />
                    </motion.a>;
              })}
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-53">
            <form onSubmit={handleSubmit} className="glass-card rounded-xl sm:rounded-2xl p-6 sm:p-8 lg:p-10" data-id="element-54">
              <div className="space-y-4 sm:space-y-6" data-id="element-55">
                <div data-id="element-56">
                  <input type="text" placeholder={t.namePlaceholder} value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-chamaleon-yellow/50 focus:bg-white/10 transition-all text-sm sm:text-base" required data-id="element-57" />
                </div>

                <div data-id="element-58">
                  <input type="email" placeholder={t.emailPlaceholder} value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-chamaleon-yellow/50 focus:bg-white/10 transition-all text-sm sm:text-base" required data-id="element-59" />
                </div>

                <div data-id="element-60">
                  <textarea placeholder={t.messagePlaceholder} value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} rows={5} className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-chamaleon-yellow/50 focus:bg-white/10 transition-all resize-none text-sm sm:text-base" required data-id="element-61" />
                </div>

                <motion.button type="submit" className="w-full group relative inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-chamaleon-yellow text-chamaleon-black font-semibold rounded-xl overflow-hidden text-sm sm:text-base" whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} data-id="element-62">
                  <span className="relative z-10" data-id="element-63">{t.submitButton}</span>
                  <SendIcon className="relative z-10 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-64" />
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>;
}